import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import type { Order, OrderCount } from '~api/tos';

import { TosApiInstance } from '~src/main/api';
import { useSiteId } from '~src/main/hooks/useSiteId';

type TransportOrdersListQueryParams = {
  pageIndex?: number;
  pageSize?: number;
} & Parameters<typeof TosApiInstance.tosListOrders>[0];

export function useTransportOrderQueryKey() {
  const siteId = useSiteId();

  const keys = {
    all: ['orders', siteId] as const,
    lists: () => [...keys.all, 'list'] as const,
    list: (filters: TransportOrdersListQueryParams) => [...keys.lists(), filters] as const,
    count: (filters: { searchQuery?: string }) => [...keys.lists(), 'count', filters],
    details: () => [...keys.all, 'detail'] as const,
    detail: (id?: string) => [...keys.details(), id] as const,
  };

  return keys;
}
export async function fetchTransportOrderDetails({ id }: Pick<Order, 'id'>) {
  const { data } = await TosApiInstance.tosGetOrder({ id });
  return data.order;
}

export function useTransportOrderDetailsSuspenseQuery({ id }: Pick<Order, 'id'>) {
  const queryKey = useTransportOrderQueryKey();

  return useSuspenseQuery({
    queryFn: async () => fetchTransportOrderDetails({ id }),
    queryKey: queryKey.detail(id),
  });
}

export function useTransportOrderDetailsQuery({ id }: Pick<Order, 'id'>) {
  const queryKey = useTransportOrderQueryKey();

  return useQuery({
    queryFn: async () => {
      const { data } = await TosApiInstance.tosGetOrder({ id });
      return data.order;
    },
    queryKey: queryKey.detail(id),
    enabled: !!id,
  });
}

export function useTransportOrdersListQuery(query: TransportOrdersListQueryParams) {
  const queryKey = useTransportOrderQueryKey();

  const pageIndex = query.pageIndex ?? 0;
  const pageSize = query.pageSize ?? 10;

  return useQuery({
    queryFn: async () => {
      const { data } = await TosApiInstance.tosListOrders({
        ...query,
        'pagination.limit': pageSize,
        'pagination.offset': pageIndex * pageSize,
      });
      return data;
    },
    queryKey: queryKey.list(query),
  });
}

export function useTransportOrdersCountQuery(params: { searchQuery?: string }) {
  const queryKey = useTransportOrderQueryKey();

  // mocked
  const defaultCounts: OrderCount[] = [
    { count: 0, source: 'DEMO' },
    { count: 0, source: 'TRACKING' },
    { count: 0, source: 'SOM' },
    { count: 0, source: 'COS' },
  ];

  return useQuery({
    queryFn: async () => {
      const { data } = await TosApiInstance.tosCountOrders({
        ...(params.searchQuery && {
          'filtering.id': params.searchQuery,
        }),
      });
      return data.counts ?? defaultCounts;
    },
    queryKey: queryKey.count(params),
  });
}
